import _ from "lodash";
import { assert } from "oc-e2e-testrunner";
import { JSON_DECIMALS } from "../commons/Utils";
import { v2, v3 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import * as Commons from "oc-commons/Commons";
import { Aufbau } from "../arrangement/Aufbau";
import { ZElement } from "../arrangement/ZElement";
import { Segment } from "../arrangement/Segment";
import {
  getSegmentEndenAusgleichForEcke,
  getSollZwischenabstandVerbose,
  getZwischenabstandAnSeiteVerbose,
  LINKS,
  RECHTS,
} from "../arrangement/elementFunctions";
import { Arrangement } from "../arrangement/Arrangement";
import { BaugruppenEintrag, BestelllistenEintrag } from "../arrangement/Stückliste";
import { Grundriss } from "../arrangement/Grundriss";
import { getTypeNameOfPart } from "../arrangement/arrangementFunctions";
import { Part } from "../arrangement/Part";
import { Bag } from "oc-commons/Bag2";

export const asDegree = 180 / Math.PI;
const round = (x) => Maths.round(x, JSON_DECIMALS);
const getX = (segment, p, side) => (p == null ? NaN : segment.getX(p, side));
const getFormX = (segment, p, side) => {
  if (p == null) {
    return NaN;
  }
  return segment.getGrundrissKanteX(segment.getWorldPositionOfX(getX(segment, p, side)));
};
export const logParts = (segment, parts) =>
  parts.map((p, i) => ({
    x0: round(getFormX(segment, p, 0)),
    x1: round(getFormX(segment, p, 1)),
    gapLinks: round(parts[i]?.gapLinks ?? 0),
    gapRechts: round(parts[i]?.gapRechts ?? 0),
    type: p.isUMProfil ? "UMProfil" : p.constructor.CLASS_NAME,
    snap: p.snap,
    breite: round(p.breite),
    lichteWeite: round(p.lichteWeite ?? 0),
    ecke: Boolean(p.ecke),
  }));

export const logPartBoxes = (parts) =>
  parts.map((p, i) => ({
    type: p.isEmpty ? "Empty" : p.meta?.Key ?? getTypeNameOfPart(p),
    box: p.getBox() + "",
  }));

export const createMauer = ({ position = v3(), size = v3(), drehung = 0 } = {}) => {
  const aufbauMeta = APP.metadata.Aufbauten.find((x) => x.Key == "Mauer");
  const newAufbau = new Aufbau(APP.arrangement, {
    Key: Commons.createUniqueIdString(),
    aufbauMeta,
    position: position,
    size: size,
    drehung: drehung,
  });
  APP.arrangement.aufbauten.push(newAufbau);
  return newAufbau;
};

export const resetArrangement = (includingDefaults = false, overwrite = {}) => {
  // eigentlich sollte hier eine Art reset immer aufgerufen werden, statt manchmal new
  if (includingDefaults) {
    APP.arrangement = new Arrangement();
  }
  APP.arrangement.fromJSON({
    inspiration: "Inspiration SYSTEM WPC CLASSIC Anthrazit",
    zaunart: "Sichtschutz",
    serie: "SYSTEM",
    grundriss: new Grundriss(APP.arrangement).setStandardForm("Rechteck", [20, 20]).toJSON(),
    segmente: [
      {
        streckenIndex: 0,
        breite: 1,
        partsAndPfosten: [],
      },
    ],
    ecken: [],
    aufbauten: [],
    pageName: "Zauneditor",
    lichtleistenSplitter: 0,
    pfostenartAuswahlwert: "Klemmpfosten",
    pfostenfarbeAuswahlwert: "Anthrazit",
    bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
    elementhalterAuswahlwert: "Elementhalter Weiß",
    verteilungsstrategieAuswahlwert: "Individuell",
    customAnzahlen: {},
    warnungen: [],
    inspirationsBilder: [],
    userDefinedArticles: [],
    hinweistexte: [],
    ...overwrite,
  });
  localStorage["isAutoCameraOn"] = false;
  APP.arrangement.selectedObjects = [];
  APP.arrangement.zauneditorReady = true;
  APP.arrangement.elementeMitBreiteWarnung.clear();
  // hier sollte ein APP.arrangement.update3d() aufgerufen werden
  APP.dirty();
  return APP.arrangement;
};

export const getMinMaxSegmentPartsX = (segment: Segment): [number, number] => {
  let x0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  segment
    .getPartsAndPfosten()
    .filter((p) => !p.isEmpty)
    .forEach((p) => {
      x0 = Math.min(x0, segment.getX(p, 0));
      x1 = Math.max(x1, segment.getX(p, 1));
    });
  return [x0, x1];
};

export function assertSegmentIstLückenlosGefüllt(s: Segment, mitSnaps = true, epsilon = 0.001) {
  const parts = s.getPartsAndPfosten();
  assert.isTrue(s.parts.length > 0, "Das Segment muss mindestens ein Element enthalten");
  assert.equals(s.parts.length + 1, s.pfosten.length, "Es muss n+1 Pfosten für n Elemente geben");
  const [x0, x1] = getMinMaxSegmentPartsX(s);

  const segmentEndenPfostenausgleich = _.sum(
    s.ecken.map((e) => getSegmentEndenAusgleichForEcke(e))
  );
  const istBreite = x1 - x0;
  const sollBreite = s.breite - segmentEndenPfostenausgleich;
  assert.equals(
    sollBreite,
    istBreite,
    `Das Segment soll in der ganzen Breite mit Elementen gefüllt sein, ohne Lücken und Überstände. Die Sollbreite ist ${Maths.round(
      sollBreite,
      epsilon
    )} m, die Zaunelemente und Pfosten insgesamt ${Maths.round(istBreite, epsilon)} m`,
    epsilon
  );
  if (mitSnaps) {
    const firstElement = s.getElemente()[0];
    assert.isTrue(
      parts.filter((p) => !p.isPfosten).every((p) => p.snap == firstElement.snap),
      "Die Elemente sollen alle in dieselbe Richtung snappen"
    );
  }
  assert.isTrue(
    parts.filter((p) => p.isPfostenAnEcke).every((p) => p.snap == 0),
    "Eckpfosten sollen snap 0 haben, also feststehen"
  );
  assert.isTrue(
    parts.filter((p) => p.isPfostenAnEcke).every((p) => p.parent === s.arrangement),
    "Eckpfosten sollen dem Arrangement gehören"
  );
  assert.isFalse(
    parts.some((p) => p.isEmpty),
    "Es darf keine Leerräume geben"
  );
  assert.equals(s.empties.length, 0, "Es darf keine Leerräume geben");

  assertKeineLückeZwischenPfostenUndElementen(s, epsilon);
}

export function assertKeineLückeZwischenPfostenUndElementen(s: Segment, epsilon = Number.EPSILON) {
  const parts = s.getPartsAndPfosten();
  let pre = null;
  let i = -1;
  for (const part of parts) {
    if (pre != null) {
      const a = s.getX(pre, 1) + pre.gapRechts;
      const b = s.getX(part, 0) - part.gapLinks;
      assert.isTrue(
        Math.abs(a - b) < epsilon,
        `zwischen dem ${getTypeNameOfPart(pre)} Index ${i} und ${getTypeNameOfPart(
          part
        )} Index ${i + 1} ist eine Lücke von ${round(b - a)} m, die es nicht geben sollte`
      );
    }
    pre = part;
    i++;
  }
}

export function verteileGleichmaessigUndAssertGleichmaessigeBreiten(
  s: Segment,
  selectedParts = s.getParts()
) {
  const agt = APP.arrangement;
  agt.verteileBauseitsKürzbareElementeGleichmäßig(selectedParts);
  assertSegmentIstLückenlosGefüllt(s);
  const parts = s.getPartsAndPfosten();
  const elemente = selectedParts.filter((p) => p.isElement && p.isBreiteVariabel && !p.isTor);
  for (const element of elemente) {
    assert.equals(elemente[0].breite, element.breite, "Alle Elemente sollen dieselbe Breite haben");
  }
  return parts;
}

export function getIstSollZwischenabstandVerbose(empty, element) {
  const linksIst = getZwischenabstandAnSeiteVerbose(empty, LINKS);
  const rechtsIst = getZwischenabstandAnSeiteVerbose(empty, RECHTS);
  const linksSoll = getSollZwischenabstandVerbose(empty, element, LINKS);
  const rechtsSoll = getSollZwischenabstandVerbose(empty, element, RECHTS);
  return { linksIst, linksSoll, rechtsIst, rechtsSoll };
}

export function assertAlleElementeSap(elemente, snap) {
  assert.isTrue(elemente.every((p) => p.snap == snap));
}

export function createStreckeWithMauerUndBreite(
  breite,
  { links = false, rechts = false, mitte = false }
) {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [breite + 1]);
  links && createMauer({ position: v3(-breite / 2 - 0.5, 0, 0), size: v3(1, 2, 0.5) });
  mitte && createMauer({ position: v3(0, 0, 0), size: v3(1, 2, 0.5) });
  rechts && createMauer({ position: v3(breite / 2 + 0.5, 0, 0), size: v3(1, 2, 0.5) });
  agt.update3d();
  const s = agt.segmente[0];
  return { agt, s };
}

export function createElement(parent: Arrangement | Segment, options = {}): ZElement {
  const agt = (parent instanceof Arrangement ? parent : parent.arrangement) as Arrangement;
  const element = agt.createDefaultElement(options);
  if (parent instanceof Segment) {
    parent.addPart(element);
    parent.setX(element, 0, 0);
  }
  return element;
}

export function assertEqualsBestellliste(
  expected: any,
  actual: BestelllistenEintrag[],
  ordered = false
) {
  const normalize = (entry) => ({
    ...entry,
    Mass: normalizeWhitespace(entry.Mass),
  });
  const actualMapped = actual.map((entry) =>
    normalize(_.pick(entry, ["ArtikelID", "Anzahl", "Mass"]))
  );
  const expectedMapped = expected.map(normalize);

  const compareEntry = (a: any, b: any) => {
    const ka = [String(a.ArtikelID ?? ""), String(a.Mass ?? ""), String(a.Anzahl ?? "")];
    const kb = [String(b.ArtikelID ?? ""), String(b.Mass ?? ""), String(b.Anzahl ?? "")];
    for (let i = 0; i < ka.length; i++) {
      if (ka[i] < kb[i]) return -1;
      if (ka[i] > kb[i]) return 1;
    }
    return 0;
  };

  if (!ordered) {
    expectedMapped.sort(compareEntry);
    actualMapped.sort(compareEntry);
  }

  assert.deepEquals(expectedMapped, actualMapped);
}

export function assertBestelllisteContains(expected: any, actual: BestelllistenEintrag[]) {
  const normalize = (entry) => ({
    ...entry,
    Mass: normalizeWhitespace(entry.Mass),
  });
  const actualMapped = actual.map((entry) =>
    normalize(_.pick(entry, ["ArtikelID", "Anzahl", "Mass"]))
  );
  const expectedNormalized = normalize(_.pick(expected, ["ArtikelID", "Anzahl", "Mass"]));
  assert.contains(expectedNormalized, actualMapped);
}

export function assertEqualsBaugruppen(expected: any, actual: BaugruppenEintrag[][]) {
  const normalize = (entry) => ({
    ...entry,
    MassOrBauseitsTag: normalizeWhitespace(entry.MassOrBauseitsTag),
  });
  const actualMapped = (actual as any).map((group) =>
    group.map((entry) => normalize(_.pick(entry, ["ArtikelID", "MassOrBauseitsTag", "Anzahl"])))
  );
  const expectedMapped = expected.map((group) => group.map(normalize));
  assert.deepEquals(expectedMapped, actualMapped);
}

export function assertElementZwischenMauern(element: Part) {
  assert.isTrue(
    (element.getNachbarn()[0] as any).anMauer && (element.getNachbarn()[1] as any).anMauer,
    "Das Elemente sollauf beiden Seiten Pfosten mit anMauer haben"
  );
}
export const normalizeWhitespace = (s) => (s == null ? s : s.replaceAll(/\s/g, " "));

export function assertWarnungen(agt: Arrangement, warnungstexte: string[]) {
  assert.deepEquals(
    warnungstexte,
    agt.warnungen.map((w) => w.text),
    "erwarte Warnung"
  );
}

export function getInspiredArrangement(inspKey) {
  resetArrangement(true);
  const agt = APP.arrangement;
  agt.inspiration = APP.richMetadata.findInspiration(inspKey);
  agt.update3d();
  return agt;
}

export function setSternFormMitWinkelnPlain(länge: number, anglesDegree: number[]) {
  const agt = APP.arrangement;
  agt.grundriss.setStrecken(
    anglesDegree.map((angleDegree) => {
      const a = v2(0, 0);
      const c = v2(länge, 0).rotateAround(a, angleDegree / asDegree);
      return [a, c];
    })
  );
}

export function setSternFormMitWinkelnLeerraumRechts(länge: number, anglesDegree: number[]) {
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("LeerraumRechts");
  setSternFormMitWinkelnPlain(länge, anglesDegree);
  agt.update3d();
  agt.update3d();
  assert.isTrue(
    agt.segmente
      .map((s) => s.verteilungsstrategieAuswahlwert.Key)
      .every((k) => k == "LeerraumRechts")
  );
  // leider sind nicht alle Segmente gleich orientiert, daher muss LeerraumLinks/Rechts angepasst werden
  const ecke = agt.ecken.find((e) => v3(0, 0, 0).distanceTo(e.position) < 0.01);
  const segmenteFalsch = ecke.segmente.filter((s) => s.ecken[1] == ecke);
  segmenteFalsch.forEach((s) => {
    s.verteilungsstrategieAuswahlwert = APP.richMetadata.findAuswahlwert("LeerraumLinks");
  });
  const segmentWerte = () =>
    agt.segmente.map((s) => ({
      id: s.id,
      ecken: s.ecken.map((e) => e.position + "").join(","),
      verteilungsstrategie: s.verteilungsstrategieAuswahlwert?.Key,
      grundrissKanteId: s.segmentKante?.grundrissKante?.id,
    }));
  const alteWerte = segmentWerte();
  // agt.segmente.forEach((s) => Debugs.watch(s, "verteilungsstrategieAuswahlwert_"));
  agt.update3d();
  const grundrissA = APP.arrangement.grundriss.toJSON();
  APP.arrangement.grundriss.update3d();
  const grundrissB = APP.arrangement.grundriss.toJSON();
  assert.deepEquals(grundrissA, grundrissB);
  // die Segmente sollten nach dem Update gleich sein, sonst läuft was falsch
  assert.deepEquals(alteWerte, segmentWerte());
}

export function warnungenMitPfostenIndex() {
  const agt = APP.arrangement;
  const pfosten = agt.getPfosten();
  return Commons.groupByFlat(
    agt.warnungen,
    (w) => w.text,
    (w) => w.jacos.map((j) => pfosten.indexOf(j as any))
  ).map((value, key) => [key, ...value.flat()]);
}

export const globalePfostenart = () =>
  (APP.arrangement.auswahlAccessoren.Pfostenart as any).auswahlwert.Key;
export const countPfostenarten = () =>
  new Bag().from(APP.arrangement.getPfosten().map((p) => p.pfostenartAuswahlwert?.Key)).toObject();
